import { ref, push, set, get, query, orderByChild, equalTo } from 'firebase/database';
import { db } from '../config/firebase';

// Firebase Database Service - replaces API service completely
export class FirebaseDatabaseService {
  
  // ==================== USER SESSIONS ====================
  
  // Create a new game session
  static async createGameSession(userId: string, gameType: string): Promise<string> {
    try {
      const sessionData = {
        userId,
        gameType,
        startTime: new Date().toISOString(),
        endTime: null,
        isActive: true,
        createdAt: new Date().toISOString()
      };
      
      const sessionsRef = ref(db, 'gameSessions');
      const newSessionRef = await push(sessionsRef, sessionData);
      
      console.log('Game session created:', newSessionRef.key);
      return newSessionRef.key!;
    } catch (error) {
      console.error('Error creating game session:', error);
      throw error;
    }
  }
  
  // End a game session
  static async endGameSession(sessionId: string): Promise<void> {
    try {
      const sessionRef = ref(db, `gameSessions/${sessionId}`);
      await set(sessionRef, {
        ...await this.getGameSession(sessionId),
        endTime: new Date().toISOString(),
        isActive: false
      });
      
      console.log('Game session ended:', sessionId);
    } catch (error) {
      console.error('Error ending game session:', error);
      throw error;
    }
  }
  
  // Get game session by ID
  static async getGameSession(sessionId: string): Promise<any> {
    try {
      const sessionRef = ref(db, `gameSessions/${sessionId}`);
      const snapshot = await get(sessionRef);
      
      if (snapshot.exists()) {
        return snapshot.val();
      }
      throw new Error(`Session ${sessionId} not found`);
    } catch (error) {
      console.error('Error getting game session:', error);
      throw error;
    }
  }
  
  // Get user's active sessions
  static async getUserActiveSessions(userId: string): Promise<any[]> {
    try {
      const sessionsRef = ref(db, 'gameSessions');
      const userSessionsQuery = query(
        sessionsRef,
        orderByChild('userId'),
        equalTo(userId)
      );
      
      const snapshot = await get(userSessionsQuery);
      if (snapshot.exists()) {
        const sessions = snapshot.val();
        return Object.keys(sessions)
          .map(key => ({ id: key, ...sessions[key] }))
          .filter(session => session.isActive);
      }
      return [];
    } catch (error) {
      console.error('Error getting user sessions:', error);
      throw error;
    }
  }
  
  // ==================== GAME RESULTS ====================
  
  // Save Number Sequence result
  static async saveNumberSequenceResult(data: any): Promise<void> {
    try {
      const resultsRef = ref(db, 'numberSequenceResults');
      const newResultRef = await push(resultsRef, {
        ...data,
        savedAt: new Date().toISOString(),
        gameType: 'NumberSequence'
      });
      console.log('NumberSequence result saved to Firebase with key:', newResultRef.key);
    } catch (error) {
      console.error('Error saving NumberSequence result:', error);
      throw error;
    }
  }
  
  // Save Memory Palace result
  static async saveMemoryPalaceResult(data: any): Promise<void> {
    try {
      const resultsRef = ref(db, 'memoryPalaceResults');
      const newResultRef = await push(resultsRef, {
        ...data,
        savedAt: new Date().toISOString(),
        gameType: 'MemoryPalace'
      });
      console.log('MemoryPalace result saved to Firebase with key:', newResultRef.key);
    } catch (error) {
      console.error('Error saving MemoryPalace result:', error);
      throw error;
    }
  }
  
  // Save Tone Memory result
  static async saveToneMemoryResult(data: any): Promise<void> {
    try {
      const resultsRef = ref(db, 'toneMemoryResults');
      const newResultRef = await push(resultsRef, {
        ...data,
        savedAt: new Date().toISOString(),
        gameType: 'ToneMemory'
      });
      console.log('ToneMemory result saved to Firebase with key:', newResultRef.key);
    } catch (error) {
      console.error('Error saving ToneMemory result:', error);
      throw error;
    }
  }
  
  // Save Image Matching result
  static async saveImageMatchingResult(data: any): Promise<void> {
    try {
      const resultsRef = ref(db, 'imageMatchingResults');
      const newResultRef = await push(resultsRef, {
        ...data,
        savedAt: new Date().toISOString(),
        gameType: 'ImageMatching'
      });
      console.log('ImageMatching result saved to Firebase with key:', newResultRef.key);
    } catch (error) {
      console.error('Error saving ImageMatching result:', error);
      throw error;
    }
  }
  
  // Save Ticker Tape result
  static async saveTickerTapeResult(data: any): Promise<void> {
    try {
      const resultsRef = ref(db, 'tickerTapeResults');
      const newResultRef = await push(resultsRef, {
        ...data,
        savedAt: new Date().toISOString(),
        gameType: 'TickerTape'
      });
      console.log('TickerTape result saved to Firebase with key:', newResultRef.key);
    } catch (error) {
      console.error('Error saving TickerTape result:', error);
      throw error;
    }
  }
  
  // ==================== USER MANAGEMENT ====================
  
  // Get user statistics
  static async getUserStats(userId: string): Promise<any> {
    try {
      // Get all game results for this user
      const gameTypes = ['numberSequenceResults', 'memoryPalaceResults', 'toneMemoryResults', 'imageMatchingResults', 'tickerTapeResults'];
      const stats: any = {
        totalGames: 0,
        gameTypeCounts: {},
        averageScores: {},
        totalTimeSpent: 0
      };
      
      for (const gameType of gameTypes) {
        const resultsRef = ref(db, gameType);
        const userResultsQuery = query(
          resultsRef,
          orderByChild('userId'),
          equalTo(userId)
        );
        
        const snapshot = await get(userResultsQuery);
        if (snapshot.exists()) {
          const results = Object.values(snapshot.val());
          stats.gameTypeCounts[gameType] = results.length;
          stats.totalGames += results.length;
          
          // Calculate average score if available
          const scores = results.map((r: any) => r.score || r.accuracyPercentage || 0);
          if (scores.length > 0) {
            stats.averageScores[gameType] = scores.reduce((a, b) => a + b, 0) / scores.length;
          }
          
          // Add up time spent
          const times = results.map((r: any) => r.completionTimeSeconds || r.timeSeconds || 0);
          stats.totalTimeSpent += times.reduce((a, b) => a + b, 0);
        } else {
          stats.gameTypeCounts[gameType] = 0;
          stats.averageScores[gameType] = 0;
        }
      }
      
      return stats;
    } catch (error) {
      console.error('Error getting user stats:', error);
      throw error;
    }
  }
  
  // Get all results for a user
  static async getUserResults(userId: string, gameType?: string): Promise<any[]> {
    try {
      if (gameType) {
        // Get results for specific game type
        const resultsRef = ref(db, `${gameType}Results`);
        const userResultsQuery = query(
          resultsRef,
          orderByChild('userId'),
          equalTo(userId)
        );
        
        const snapshot = await get(userResultsQuery);
        if (snapshot.exists()) {
          const results = snapshot.val();
          return Object.keys(results).map(key => ({ id: key, ...results[key] }));
        }
      } else {
        // Get all results for user across all game types
        const gameTypes = ['numberSequence', 'memoryPalace', 'toneMemory', 'imageMatching', 'tickerTape'];
        const allResults: any[] = [];
        
        for (const type of gameTypes) {
          const results = await this.getUserResults(userId, type);
          allResults.push(...results);
        }
        
        return allResults.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
      }
      
      return [];
    } catch (error) {
      console.error('Error getting user results:', error);
      throw error;
    }
  }
  
  // ==================== DASHBOARD DATA ====================
  
  // Get dashboard data for admin/researcher
  static async getDashboardData(): Promise<any> {
    try {
      const gameTypes = ['numberSequenceResults', 'memoryPalaceResults', 'toneMemoryResults', 'imageMatchingResults', 'tickerTapeResults'];
      const dashboardData: any = {
        totalUsers: 0,
        totalGames: 0,
        gameTypeStats: {},
        recentActivity: []
      };
      
      // Count users
      const usersRef = ref(db, 'users');
      const usersSnapshot = await get(usersRef);
      if (usersSnapshot.exists()) {
        dashboardData.totalUsers = Object.keys(usersSnapshot.val()).length;
      }
      
      // Get game statistics
      for (const gameType of gameTypes) {
        const resultsRef = ref(db, gameType);
        const snapshot = await get(resultsRef);
        
        if (snapshot.exists()) {
          const results = Object.values(snapshot.val());
          dashboardData.totalGames += results.length;
          dashboardData.gameTypeStats[gameType] = {
            count: results.length,
            averageScore: results.reduce((acc: number, r: any) => acc + (r.score || r.accuracyPercentage || 0), 0) / results.length
          };
          
          // Add to recent activity
          const recentResults = Object.keys(snapshot.val())
            .map(key => ({ id: key, ...snapshot.val()[key] }))
            .sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime())
            .slice(0, 5);
          
          dashboardData.recentActivity.push(...recentResults);
        } else {
          dashboardData.gameTypeStats[gameType] = { count: 0, averageScore: 0 };
        }
      }
      
      // Sort recent activity by date
      dashboardData.recentActivity.sort((a: any, b: any) => 
        new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime()
      );
      dashboardData.recentActivity = dashboardData.recentActivity.slice(0, 20);
      
      return dashboardData;
    } catch (error) {
      console.error('Error getting dashboard data:', error);
      throw error;
    }
  }
}